import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Polls unread chat + announcement counters every 20s so the WhatsApp-style
 * red badges stay in sync without a websocket. Pauses when the tab is
 * hidden to save requests. Admin and company accounts hit different
 * endpoints under the hood.
 */
export function useUnreadCounts() {
    const { company } = useAuth();
    const [chat, setChat] = useState(0);
    const [news, setNews] = useState(0);
    const [pending, setPending] = useState(0);

    const refresh = useCallback(async () => {
        if (!company || company === false) return;
        try {
            if (company.role === "admin") {
                const [c, p] = await Promise.all([
                    api.get("/admin/chat/unread-count").catch(() => ({ data: { unread: 0 } })),
                    api.get("/admin/subscriptions/pending").catch(() => ({ data: { items: [] } })),
                ]);
                setChat(c.data?.unread || 0);
                setPending((p.data?.items || []).length);
                setNews(0);
            } else {
                const [c, n] = await Promise.all([
                    api.get("/chat/unread-count").catch(() => ({ data: { unread: 0 } })),
                    api.get("/announcements/unread-count").catch(() => ({ data: { unread: 0 } })),
                ]);
                setChat(c.data?.unread || 0);
                setNews(n.data?.unread || 0);
                setPending(0);
            }
        } catch { /* ignore transient errors */ }
    }, [company]);

    useEffect(() => {
        if (!company) return undefined;
        refresh();
        const id = setInterval(() => {
            if (document.visibilityState === "visible") refresh();
        }, 20000);
        const onVis = () => { if (document.visibilityState === "visible") refresh(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [company, refresh]);

    return { chat, news, pending, refresh };
}

/** Compact red circle badge used next to nav icons. */
export function UnreadBadge({ count, testid }) {
    if (!count || count <= 0) return null;
    const shown = count > 99 ? "99+" : String(count);
    return (
        <span
            className="pointer-events-none absolute -top-0.5 -end-0.5 flex min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white ring-2 ring-white"
            data-testid={testid}
        >
            {shown}
        </span>
    );
}
